import { useRef, useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { Modal, Button, useI18n } from '@ywwwtseng/react-kit';

export interface DatePickerProps {
  className?: string;
  disabled?: boolean;
  label?: string;
  value: string | null;
  onChange: (value: string | null) => void;
}

export function parseFormDate(value: string | null): string | null {
  if (value === null || value === '') {
    return null;
  }
  // 验证日期格式 YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) {
    return null;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return null;
  }
  return value;
}

// 获取指定年月的天数
function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

// 判断是否为闰年
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

// 格式化日期显示
function formatDateDisplay(year: number | null, month: number | null, day: number | null): string {
  if (year === null || month === null || day === null) {
    return '';
  }
  const yearStr = String(year);
  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return `${yearStr}-${monthStr}-${dayStr}`;
}

// 滚动选择器组件
function ScrollPicker({
  items,
  value,
  onChange,
  label,
  isOpen,
}: {
  items: { value: number; label: string }[];
  value: number | null;
  onChange: (value: number) => void;
  label: string;
  isOpen?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const itemHeight = 40;
  const visibleItems = 5;
  const containerHeight = itemHeight * visibleItems;

  useEffect(() => {
    if (containerRef.current && value !== null && isOpen) {
      const index = items.findIndex((item) => item.value === value);
      if (index !== -1) {
        // 让选中项显示在中间位置（选中区域在 itemHeight * 2 的位置）
        // 所以 scrollTop 应该是 index * itemHeight，这样选中项就会在中间
        const scrollTop = index * itemHeight;
        // 标记为程序化滚动，避免触发 handleScroll
        isScrollingRef.current = true;
        setCurrentIndex(index);
        // 使用双重 requestAnimationFrame 确保 Modal 完全打开后再滚动
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = scrollTop;
              // 滚动完成后重置标志
              setTimeout(() => {
                isScrollingRef.current = false;
              }, 100);
            }
          });
        });
      }
    }
  }, [value, items, itemHeight, isOpen]);

  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = useCallback(() => {
    // 如果是程序化滚动，跳过处理
    if (isScrollingRef.current || !containerRef.current) return;

    const scrollTop = containerRef.current.scrollTop;
    // 选中区域在 itemHeight * 2 的位置
    // 所以需要计算哪个选项当前在中间位置
    const index = Math.max(0, Math.round(scrollTop / itemHeight));

    // 立即更新当前索引，用于显示白色
    setCurrentIndex(index);

    // 清除之前的定时器
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // 延迟处理，避免滚动时频繁更新 onChange
    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current || isScrollingRef.current) return;

      const selectedItem = items[index];
      if (selectedItem && selectedItem.value !== value) {
        onChange(selectedItem.value);
      }
    }, 50);
  }, [items, value, onChange, itemHeight]);

  // 处理滚动结束，确保对齐到中间
  const handleScrollEnd = useCallback(() => {
    if (isScrollingRef.current || !containerRef.current) return;

    // 清除滚动定时器
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    const scrollTop = containerRef.current.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    const targetScrollTop = index * itemHeight;

    // 立即更新当前索引
    setCurrentIndex(index);

    // 如果不在正确位置，对齐到最近的选项
    if (Math.abs(scrollTop - targetScrollTop) > 2) {
      isScrollingRef.current = true;
      containerRef.current.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 300);
    } else {
      // 即使位置正确，也更新选中值
      const selectedItem = items[index];
      if (selectedItem && selectedItem.value !== value) {
        onChange(selectedItem.value);
      }
    }
  }, [itemHeight, items, value, onChange]);

  return (
    <div className="flex-1 flex flex-col items-center">
      <div className="text-xs text-[#777777] mb-2">{label}</div>
      <div className="relative w-full" style={{ height: containerHeight }}>
        {/* 选中区域高亮 */}
        <div
          className="absolute left-0 right-0 bg-form-element/50 rounded pointer-events-none"
          style={{
            top: itemHeight * 2,
            height: itemHeight,
            zIndex: 0,
          }}
        />
        {/* 滚动容器 */}
        <div
          ref={containerRef}
          className="w-full h-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          onScroll={handleScroll}
          onTouchEnd={handleScrollEnd}
          onMouseUp={handleScrollEnd}
          style={{
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* 顶部占位 */}
          <div style={{ height: itemHeight * 2 }} />
          {/* 选项列表 */}
          {items.map((item, index) => {
            const isSelected = currentIndex !== null && index === currentIndex;
            return (
              <div
                key={item.value}
                className="flex items-center justify-center text-sm relative"
                style={{
                  height: itemHeight,
                  zIndex: 1,
                }}
                onClick={() => {
                  if (containerRef.current && index !== -1) {
                    // 标记为程序化滚动
                    isScrollingRef.current = true;
                    setCurrentIndex(index);
                    // 滚动到让该项显示在中间位置
                    containerRef.current.scrollTo({
                      top: index * itemHeight,
                      behavior: 'smooth',
                    });
                    // 滚动完成后重置标志
                    setTimeout(() => {
                      isScrollingRef.current = false;
                    }, 300);
                  }
                }}
              >
                <span
                  className={clsx(
                    'block',
                    isSelected
                      ? 'text-white font-semibold'
                      : 'text-[#777777]'
                  )}
                  style={{
                    lineHeight: `${itemHeight}px`,
                    height: `${itemHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                    transformOrigin: 'center',
                    transition: 'color 0.1s, transform 0.1s',
                  }}
                >
                  {item.label}
                </span>
              </div>
            );
          })}
          {/* 底部占位 */}
          <div style={{ height: itemHeight * 2 }} />
        </div>
      </div>
    </div>
  );
}

export function DatePicker({
  className,
  disabled,
  label,
  value,
  onChange,
}: DatePickerProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // 解析当前值
  useEffect(() => {
    if (value) {
      const parsed = parseFormDate(value);
      if (parsed) {
        const [year, month, day] = parsed.split('-').map(Number);
        setSelectedYear(year);
        setSelectedMonth(month);
        setSelectedDay(day);
      }
    } else {
      // 如果没有值，默认设置为当前年份-20年，1月1日
      const now = new Date();
      setSelectedYear(now.getFullYear() - 20);
      setSelectedMonth(1);
      setSelectedDay(1);
    }
  }, [value, isOpen]);

  // 生成年份列表（从120年前到当前年份，适合出生日期等场景）
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 120; // 120年前
  const maxYear = currentYear; // 当前年份，不允许选择未来
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map((year) => ({
    value: year,
    label: String(year),
  }));

  // 生成月份列表
  const months = Array.from({ length: 12 }, (_, i) => i + 1).map((month) => ({
    value: month,
    label: String(month).padStart(2, '0'),
  }));

  // 生成日期列表（根据选中的年月动态生成）
  const daysInMonth =
    selectedYear !== null && selectedMonth !== null
      ? getDaysInMonth(selectedYear, selectedMonth)
      : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => ({
    value: day,
    label: String(day).padStart(2, '0'),
  }));

  // 当月份或年份变化时，调整日期
  useEffect(() => {
    if (selectedYear !== null && selectedMonth !== null && selectedDay !== null) {
      const maxDay = getDaysInMonth(selectedYear, selectedMonth);
      if (selectedDay > maxDay) {
        setSelectedDay(maxDay);
      }
    }
  }, [selectedYear, selectedMonth]);

  const handleConfirm = () => {
    if (selectedYear !== null && selectedMonth !== null && selectedDay !== null) {
      const formatted = formatDateDisplay(selectedYear, selectedMonth, selectedDay);
      onChange(formatted);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
  };

  const displayText = value
    ? (() => {
      const parsed = parseFormDate(value);
      if (parsed) {
        const [year, month, day] = parsed.split('-');
        return `${year}-${month}-${day}`;
      }
      return '';
    })()
    : '';

  return (
    <>
      <div className={clsx('w-full relative', className)}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(true)}
          className={clsx(
            'w-full h-10 bg-form-element rounded-lg',
            'text-sm text-right px-3',
            'border-none outline-none',
            'flex items-center justify-end',
            displayText ? 'text-foreground' : 'text-[#777777]',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && 'cursor-pointer touch-manipulation',
            'transition-colors'
          )}
          style={{
            minHeight: '40px',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {displayText || t('common.select_date')}
        </button>
        {/* 清除按钮 */}
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-[#777777] hover:text-foreground transition-colors z-10 p-1"
            aria-label={t('common.clear')}
            style={{
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={label ?? t('common.select_date')}
        classes={{
          content: 'max-h-[80vh]',
        }}
      >
        <div className="flex flex-col w-full">
          {/* 滚动选择器区域 */}
          <div className="flex w-full px-4 py-6" style={{ minHeight: '300px' }}>
            <ScrollPicker
              items={years}
              value={selectedYear}
              onChange={setSelectedYear}
              label={t('common.date_picker.year')}
              isOpen={isOpen}
            />
            <ScrollPicker
              items={months}
              value={selectedMonth}
              onChange={setSelectedMonth}
              label={t('common.date_picker.month')}
              isOpen={isOpen}
            />
            <ScrollPicker
              items={days}
              value={selectedDay}
              onChange={setSelectedDay}
              label={t('common.date_picker.day')}
              isOpen={isOpen}
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2 px-4 pb-4">
            {/* <Button
              width="full"
              size="sm"
              rounded="full"
              variant="text"
              onClick={handleClear}
              className="flex-1"
            >
              {t('common.clear')}
            </Button> */}
            <Button
              width="full"
              size="sm"
              // size="sm"
              rounded="full"
              color="primary"
              variant="contained"
              onClick={handleConfirm}
              className="flex-1"
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
